import { useEffect, useMemo, useRef, useState } from 'react';
import { Moon, Sun, Sunrise, type LucideIcon } from 'lucide-react';
import type { AvailabilitySlot } from '@rx-manager/shared';
import { intlLocale, useI18n } from '../lib/i18n';
import { cn, FOCUS_RING } from './ui';

/**
 * The grid of open times.
 *
 * The single most accessibility-sensitive control on the site, and the one most
 * booking pages get wrong. Four rules, each from a specific failure:
 *
 * 1. **Roving tabindex.** Exactly one button is in the tab order; the arrow keys
 *    move between the rest. Forty individually tabbable buttons means a keyboard
 *    user presses Tab forty times to reach the Continue button — technically
 *    operable, practically unusable.
 * 2. **Choosing never submits.** A pick that auto-advances is a trap for a
 *    screen-reader user who is exploring options, and for anyone who fat-fingers
 *    one on a phone. Continue is always a separate, deliberate press.
 * 3. **The count is announced.** Changing the date replaces the whole grid; an
 *    `aria-live` region says how many times are now offered, because the visual
 *    change is invisible to a screen reader.
 * 4. **Focus follows the arrow keys.** Moving the roving index without moving
 *    real focus leaves the screen reader announcing the old button.
 *
 * Rendered as a listbox rather than a group of buttons: "3 of 12 selected" is
 * what a patient needs to hear, and it is what the role gives for free.
 *
 * The times come in morning, afternoon and evening groups (the waitlist's cut-offs: noon and
 * five), as MedEssist shows them: forty-eight times in one wall is hard to scan, three short rows
 * are not. Still one listbox with one tab stop; the arrows cross from one group into the next.
 */

export interface SlotGridProps {
  slots: AvailabilitySlot[];
  /** The location's zone. Never the phone's — the appointment is at the store. */
  timeZone: string;
  selected: string | null;
  onSelect: (startUtc: string) => void;
  /** Localised `aria-label` for the whole grid. */
  label: string;
}

type DayPart = 'morning' | 'afternoon' | 'evening';

const PART_ICON: Record<DayPart, LucideIcon> = { morning: Sunrise, afternoon: Sun, evening: Moon };

export function SlotGrid({
  slots,
  timeZone,
  selected,
  onSelect,
  label,
}: SlotGridProps) {
  const { t, locale } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  /**
   * Only steal focus when the patient moved with the keyboard. Focusing on
   * every render would rip focus away from whatever they were doing when a
   * background refetch landed.
   */
  const shouldFocus = useRef(false);

  // In the page's language, as "Next available" above it says the time.
  const formatTime = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale(locale), {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }),
    [timeZone, locale],
  );

  /** Each time with its place in the whole list, in its part of the day (in the branch's zone). */
  const groups = useMemo(() => {
    const hourOf = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone });
    const parts: { part: DayPart; items: { slot: AvailabilitySlot; index: number }[] }[] = [];
    slots.forEach((slot, index) => {
      const hour = Number(hourOf.format(new Date(slot.startUtc)));
      const part: DayPart = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
      let group = parts.find((entry) => entry.part === part);
      if (!group) {
        group = { part, items: [] };
        parts.push(group);
      }
      group.items.push({ slot, index });
    });
    return parts;
  }, [slots, timeZone]);

  // A new day means a new grid; start from the selection, or the top.
  useEffect(() => {
    const index = slots.findIndex((slot) => slot.startUtc === selected);
    setActiveIndex(index >= 0 ? index : 0);
    shouldFocus.current = false;
  }, [slots, selected]);

  useEffect(() => {
    if (!shouldFocus.current) return;
    shouldFocus.current = false;
    const buttons = containerRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    buttons?.[activeIndex]?.focus();
  }, [activeIndex]);

  if (slots.length === 0) return null;

  const options = (): HTMLElement[] =>
    Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);

  const move = (next: number): void => {
    shouldFocus.current = true;
    setActiveIndex(Math.min(Math.max(next, 0), slots.length - 1));
  };

  /**
   * Column count is read from the rendered layout rather than hard-coded, so
   * Up/Down move a visual row on a phone and on a desktop alike.
   */
  const columnsPerRow = (): number => {
    const buttons = options();
    if (buttons.length < 2) return 1;
    const top = buttons[0]!.offsetTop;
    let count = 0;
    for (const button of buttons) {
      if (button.offsetTop !== top) break;
      count += 1;
    }
    return Math.max(count, 1);
  };

  /**
   * The time straight above or below, across a group's edge too: the nearest row in that
   * direction, then the nearest time in it. Null where layout is not known (or there is none).
   */
  const verticalNeighbour = (direction: 1 | -1): number | null => {
    const buttons = options();
    const here = buttons[activeIndex]?.getBoundingClientRect();
    if (!here) return null;
    let best: number | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    buttons.forEach((button, index) => {
      const there = button.getBoundingClientRect();
      const down = (there.top - here.top) * direction;
      if (down <= 1) return;
      const score = down * 1000 + Math.abs(there.left - here.left);
      if (score < bestScore) {
        bestScore = score;
        best = index;
      }
    });
    return best;
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    switch (event.key) {
      case 'ArrowRight':
        move(activeIndex + 1);
        break;
      case 'ArrowLeft':
        move(activeIndex - 1);
        break;
      case 'ArrowDown':
        move(verticalNeighbour(1) ?? activeIndex + columnsPerRow());
        break;
      case 'ArrowUp':
        move(verticalNeighbour(-1) ?? activeIndex - columnsPerRow());
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(slots.length - 1);
        break;
      case 'Enter':
      case ' ':
        onSelect(slots[activeIndex]!.startUtc);
        break;
      default:
        return; // let everything else through, including Tab
    }
    // Only for keys handled above, so Tab and typing still work normally.
    event.preventDefault();
  };

  return (
    <div ref={containerRef} role="listbox" aria-label={label} onKeyDown={onKeyDown} className="space-y-5">
      {groups.map((group) => {
        const Icon = PART_ICON[group.part];
        return (
          // The group's name comes from aria-label; the visible heading is the same words, hidden
          // from the accessibility tree so a listbox holds nothing but its groups and options.
          <div key={group.part} role="group" aria-label={t(`time.part.${group.part}`)}>
            <div aria-hidden="true" className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-heading">
              <Icon className="h-4 w-4 text-primary-strong" />
              {t(`time.part.${group.part}`)}
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {group.items.length}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {group.items.map(({ slot, index }) => {
                const isSelected = slot.startUtc === selected;
                return (
                  <button
                    key={slot.startUtc}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    // Roving: one stop in the tab order, arrows for the rest.
                    tabIndex={index === activeIndex ? 0 : -1}
                    onClick={() => {
                      setActiveIndex(index);
                      onSelect(slot.startUtc);
                    }}
                    className={cn(
                      'min-h-12 rounded-lg border px-2 text-base font-medium tabular-nums transition-colors',
                      FOCUS_RING,
                      isSelected
                        ? 'border-primary bg-primary font-semibold text-primary-foreground shadow-sm'
                        : 'border-border bg-card text-heading hover:border-primary hover:bg-accent',
                    )}
                  >
                    {formatTime.format(new Date(slot.startUtc))}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
